import { framesLookIdentical, type FramePixels } from './framesLookIdentical';

// Long side of the comparison grid: coarse enough to ignore JPEG and
// resampling noise, fine enough that a corner watermark covers several cells.
const GRID_LONG_SIDE = 64;
// Decoding two data URLs is instant in practice; never let it stall the flow.
const LOAD_TIMEOUT_MS = 3000;

function loadPixels(dataUrl: string, width: number, height: number): Promise<FramePixels> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('frame image load timed out')), LOAD_TIMEOUT_MS);
    const image = new Image();
    image.onload = () => {
      clearTimeout(timer);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('no 2d context'));
        return;
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, 0, 0, width, height);
      resolve(ctx.getImageData(0, 0, width, height));
    };
    image.onerror = () => {
      clearTimeout(timer);
      reject(new Error('frame image failed to load'));
    };
    image.src = dataUrl;
  });
}

/** Whether two cover-frame candidates (data URLs, same export aspect) look
 *  the same. Any failure answers false, which keeps the chooser. */
export async function dataUrlsLookIdentical(a: string, b: string, exportAspect: number): Promise<boolean> {
  try {
    const portrait = exportAspect < 1;
    const width = portrait ? Math.round(GRID_LONG_SIDE * exportAspect) : GRID_LONG_SIDE;
    const height = portrait ? GRID_LONG_SIDE : Math.round(GRID_LONG_SIDE / exportAspect);
    const [pa, pb] = await Promise.all([loadPixels(a, width, height), loadPixels(b, width, height)]);
    return framesLookIdentical(pa, pb);
  } catch {
    return false;
  }
}
